import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { buildVariantOptions, groupVariantsByProduct } from '@/lib/productVariantOptions';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Package, Tag, ChevronRight, X, ImageIcon, Plus, Upload, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ProductForm from '@/components/products/ProductForm';
import ProductImporter from '@/components/products/ProductImporter';
import { thumb, IMG_PROPS } from '@/lib/imageUrl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AgentCatalog() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showManageSheet, setShowManageSheet] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const queryClient = useQueryClient();

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => (await base44.entities.Product.list('name')).filter((p) => p.is_active !== false && p.aktiv !== false),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: variantRows = [] } = useQuery({
    queryKey: ['product-variants'],
    queryFn: () => base44.entities.ProductVariant.list('position'),
  });
  const variantsByProduct = useMemo(() => groupVariantsByProduct(variantRows), [variantRows]);
  const selectedVariants = selectedProduct
    ? buildVariantOptions(selectedProduct, variantsByProduct.get(selectedProduct.id))
    : [];

  const createProductMutation = useMutation({
    mutationFn: (data) => base44.entities.Product.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      setShowManageSheet(false);
      setEditingProduct(null);
      toast.success('Produkt erstellt');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const updateProductMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Product.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      setShowManageSheet(false);
      setEditingProduct(null);
      toast.success('Produkt aktualisiert');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id) => base44.entities.Product.update(id, { active: false }),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      setDeleteProduct(null);
      toast.success('Produkt gelöscht');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const handleSubmitProduct = (data) => {
    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, data });
    } else {
      createProductMutation.mutate(data);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowManageSheet(true);
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const cat = product.category || 'Sonstige';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-brand-950 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">Katalog</h1>
              <p className="text-sm text-slate-500">{products.length} Produkte</p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setEditingProduct(null);
                setShowManageSheet(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Verwalten
            </Button>
          </div>

          {/* Search & Filter */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Produkt suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Product List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="p-3">
                <Skeleton className="h-32 w-full rounded-lg mb-2" />
                <Skeleton className="h-5 w-3/4 mb-1" />
                <Skeleton className="h-4 w-1/2" />
              </Card>
            ))}
          </div>
        ) : Object.keys(groupedProducts).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedProducts).map(([category, prods]) => (
              <div key={category}>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {category}
                  <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">
                    {prods.length}
                  </span>
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {prods.map(product => (
                    <Card
                      key={product.id}
                      className="overflow-hidden hover:shadow-md transition-shadow group relative"
                    >
                      <div
                        className="aspect-square bg-slate-100 relative cursor-pointer"
                        onClick={() => setSelectedProduct(product)}
                      >
                        {product.image_url ? (
                          <img
                            {...IMG_PROPS}
                            src={thumb(product.image_url, 400)}
                            alt={product.name}
                            width={400}
                            height={400}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-slate-300" />
                          </div>
                        )}
                        {/* Edit/Delete always visible on touch, hover-revealed on desktop */}
                        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="secondary"
                            className="h-7 w-7 bg-white/95 hover:bg-white shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProduct(product);
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="secondary"
                            className="h-7 w-7 bg-white/95 hover:bg-white shadow-sm text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteProduct(product);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="p-3" onClick={() => setSelectedProduct(product)}>
                        <h3 className="font-semibold text-slate-900 line-clamp-1">{product.name}</h3>
                        <p className="text-xs text-slate-500 mb-2">{product.sku}</p>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-lg text-emerald-600">
                            €{product.price?.toFixed(2)}
                          </span>
                          {product.unit && (
                            <span className="text-xs text-slate-500">/{product.unit}</span>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">Keine Produkte gefunden</p>
            <p className="text-sm text-slate-400">Versuchen Sie eine andere Suche</p>
          </Card>
        )}
      </div>

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="w-[95vw] max-w-md p-0 overflow-hidden rounded-2xl">
          {selectedProduct && (
            <>
              <div className="aspect-video bg-slate-100 relative">
                {selectedProduct.image_url ? (
                  <img
                    {...IMG_PROPS}
                    src={thumb(selectedProduct.image_url, 800)}
                    alt={selectedProduct.name}
                    width={800}
                    height={450}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-20 h-20 text-slate-300" />
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{selectedProduct.name}</h2>
                    <p className="text-sm text-slate-500">{selectedProduct.sku}</p>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600">
                    €{selectedProduct.price?.toFixed(2)}
                  </span>
                </div>

                {selectedProduct.description && (
                  <p className="text-slate-600 mb-4">{selectedProduct.description}</p>
                )}

                {selectedVariants.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-sm text-slate-500 mb-2">Varianten</h3>
                    <div className="space-y-2">
                      {selectedVariants.map((variant) => (
                        <div key={variant.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{variant.value}</p>
                            {variant.sku && <p className="text-xs text-slate-500">{variant.sku}</p>}
                          </div>
                          {variant.price != null && (
                            <span className="font-bold text-emerald-600">€{variant.price.toFixed(2)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 text-sm text-slate-500">
                  {selectedProduct.category && (
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      {selectedProduct.category}
                    </span>
                  )}
                  {selectedProduct.unit && (
                    <span>Einheit: {selectedProduct.unit}</span>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Products Sheet */}
      <Sheet open={showManageSheet} onOpenChange={setShowManageSheet}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>
              {editingProduct ? 'Produkt bearbeiten' : 'Produkte verwalten'}
            </SheetTitle>
          </SheetHeader>

          {editingProduct ? (
            <ProductForm
              product={editingProduct}
              onSubmit={handleSubmitProduct}
              onCancel={() => {
                setEditingProduct(null);
                setShowManageSheet(false);
              }}
            />
          ) : (
            <Tabs defaultValue="add" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="add">
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </TabsTrigger>
                <TabsTrigger value="import">
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </TabsTrigger>
              </TabsList>
              <TabsContent value="add" className="mt-4">
                <ProductForm
                  onSubmit={handleSubmitProduct}
                  onCancel={() => setShowManageSheet(false)}
                />
              </TabsContent>
              <TabsContent value="import" className="mt-4">
                <ProductImporter
                  onSuccess={() => {
                    queryClient.invalidateQueries(['products']);
                    setShowManageSheet(false);
                  }}
                />
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{deleteProduct?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteProductMutation.mutate(deleteProduct.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
